import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import ReactPlayer from 'react-player';
import Paragraph from "../Paragraph";
import {fieldObjectSectionStyle} from '../../helpers/graphQLShortcuts';
import {fieldObjectSectionStyleCss} from '../../helpers/createStyles';
import {filterHeadline, filterUrl} from "../../helpers/filters";
import {getVideoId} from "../../helpers/video";
import './InnerHeroItem.scss';
import {trackEventByUrl} from "../../helpers/events";

interface Data {
    paragraphById: {
        fieldTxtHeadline: string;
        fieldTxtSubTitle: string;
        fieldAutoplayAsBackgroundVid: boolean;
        fieldVarVideoLink: Array<{
            title: string;
            url: Array<{
                routed: boolean;
                path: string;
            }>;
        }>;
        fieldObjectHeroItemStyle: Array<{entity: Array}>;
        fieldCtaItem: Array<{targetRevisionId: string}>;
    }
}

class InnerHeroItemQuery extends Query<Data, {id: string}> {}

const GET_COMPONENT = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    ... on Paragraph {
      ... on Paragraph {
        ... on ParagraphInnerHeroItem {
          fieldTxtHeadline
          fieldTxtSubTitle
          fieldAutoplayAsBackgroundVid
          fieldBackgroundVideoAspectRa
          fieldVarVideoLink {
            title
            url {
              routed
              path
            }
          }
          fieldObjectHeroItemStyle {
              ${fieldObjectSectionStyle}
          }
          fieldCtaItem {
            targetRevisionId
          }
        }
      }
    }
  }
}
`;


class FieldInnerHeroItem extends Component {

    static propTypes = {
        id: PropTypes.number.isRequired,
        componentIndex: PropTypes.number,
        index: PropTypes.number,
        markComponentLoadedAs: PropTypes.func.isRequired,
        handleLoadedComponents: PropTypes.func.isRequired
    };

    static defaultProps = {
        componentIndex: null,
        index: null
    };

    state = {
        videoStatus: 'playing'
    }

    getYoutubeEmbedId = url => {
        if (!url || !url.includes('youtube.com') || url.includes('youtu.be')) return false;

        return url.split('?v=')[1];
    };

    toggleBackgroundVideo = () => {
        if(this.state.videoStatus === 'playing') {
          this.setState({videoStatus: 'paused'});
          this.stopAllYouTubeVideos();
        } else {
          this.setState({videoStatus: 'playing'});
          this.playAllYouTubeVideos();;
        }
    }
  
    stopAllYouTubeVideos = () => { 
        const iframes = document.querySelectorAll('iframe');
        Array.prototype.forEach.call(iframes, iframe => { 
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'stopVideo' }), '*');
       });
    }
  
    playAllYouTubeVideos = () => { 
        const iframes = document.querySelectorAll('iframe');
        Array.prototype.forEach.call(iframes, iframe => { 
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo' }), '*');
       });
    }

    renderTitle = (title, isFirstSlideAndFirstComponent=false) => {
        if (!title) return null;

        if (isFirstSlideAndFirstComponent) {
            // eslint-disable-next-line react/no-danger
            return (<h1 className="fieldInnerHeroItem__title" dangerouslySetInnerHTML={{__html: filterHeadline(title)}} />);
        }
        // eslint-disable-next-line react/no-danger
        return (<h2 className="fieldInnerHeroItem__title" dangerouslySetInnerHTML={{__html: filterHeadline(title)}} />);
    };
    
    
    render() {
        const {id, markComponentLoadedAs, handleLoadedComponents} = this.props;

        return (
            <InnerHeroItemQuery asyncMode query={GET_COMPONENT} variables={{ id: this.props.id }}>
                {({ loading, error, data }) => {
                    if (loading) return null;
                    if (error || !data.paragraphById) {
                        markComponentLoadedAs(id, true);
                        return <div>{error || null}</div>;
                    }

                    const field = data.paragraphById;

                    const styles = fieldObjectSectionStyleCss(field.fieldObjectHeroItemStyle);

                    // Determine whether to use an H1
                    const isFirstSlideAndFirstComponent = this.props.componentIndex === 0 && this.props.index === 0;

                    const renderInner = () => (
                        <div className={`fieldInnerHeroItem${styles.classNames}`} style={styles.css} id={styles.id}>
                            {!field.fieldAutoplayAsBackgroundVid && styles.childBackground.isActive && (
                                <div style={styles.childBackground.css}/>
                            )}


                            <div className="container">
                                {this.renderTitle(field.fieldTxtHeadline, isFirstSlideAndFirstComponent)}

                                {field.fieldTxtSubTitle && isFirstSlideAndFirstComponent && (
                                    // eslint-disable-next-line react/no-danger
                                    <h2 className="hero__subTitle" style={{fontSize: '24px'}} dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                                )}
                                {field.fieldTxtSubTitle && !isFirstSlideAndFirstComponent && (
                                    // eslint-disable-next-line react/no-danger
                                    <h3 className="hero__subTitle" dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtSubTitle)}} />
                                )}

                                {field.fieldCtaItem && (
                                    <div className="fieldInnerHeroItem__ctas">
                                        {field.fieldCtaItem.map(component => (
                                            <Paragraph key={`${this.props.id}_${component.targetRevisionId}`}
                                                       id={String(component.targetRevisionId)}
                                                       handleLoadedComponents={handleLoadedComponents}/>
                                        ))}
                                    </div>
                                )}

                                {!field.fieldAutoplayAsBackgroundVid && field.fieldVarVideoLink && (
                                    <a onClick={() => {
                                        trackEventByUrl(field.fieldVarVideoLink.url.path, field.fieldVarVideoLink.title);
                                    }} className="cta" href={filterUrl(field.fieldVarVideoLink.url.path)}>
                                        {field.fieldVarVideoLink.title}
                                    </a>
                                )}

                            </div>
                        </div>
                    );

                    markComponentLoadedAs(id, true);

                    let heroVideoId = null;
                    let heroVideoUrl = null;
                    if(field.fieldAutoplayAsBackgroundVid && field.fieldVarVideoLink) {
                        heroVideoId = getVideoId(field.fieldVarVideoLink.url.path);
                        heroVideoUrl = heroVideoId.type === 'vimeo' ? `https://player.vimeo.com/video/${heroVideoId.id}?title=0&byline=0&portrait=0&autoplay=1` : field.fieldVarVideoLink.url.path;
                    }

                    const aspectRatio = field.fieldBackgroundVideoAspectRa ? field.fieldBackgroundVideoAspectRa.replace(':', '-') : 'default';

                    return (
                        field.fieldAutoplayAsBackgroundVid && field.fieldVarVideoLink ? (
                            <div className={`fieldInnerHeroItemVideo videoHero__container videoHero__container--${aspectRatio} videoHero__container--${heroVideoId.type}`}>
                                <div className="videoHero">
                                    <ReactPlayer 
                                        className="videoHero__player fieldInnerHeroItemVideo__player"
                                        controls={false}
                                        autoplay
                                        width='100%'
                                        height='100%'
                                        loop
                                        url={heroVideoUrl}
                                        muted
                                        playing={this.state.videoStatus === 'playing'}
                                    />
                                    <div className="videoHero__overlay" />
                                </div>
                                <div className="videoHero__textContainer">
                                    <div className="fieldInnerHeroItemVideo__content videoHero__text">
                                        {renderInner()}
                                        {field.fieldAutoplayAsBackgroundVid && field.fieldVarVideoLink && (
                                            <button className={`videoHero__button${this.state.videoStatus === 'playing' ? '' : ' videoHero__button--play'}`} onClick={this.toggleBackgroundVideo} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : renderInner()
                    );
                }}
            </InnerHeroItemQuery>
        );
    }
}

export default FieldInnerHeroItem;



// WEBPACK FOOTER //
// ./frontend/components/Hero/InnerHeroItem.jsx